import { Directive, ElementRef, HostListener, Injector, Input, OnDestroy, OnInit, Optional, SkipSelf } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, NgForm } from '@angular/forms';
import { AppFormControlDirective } from './app-form-control.directive';
import { AppShowErrorsDirective } from './show-errors.directive';

const resolvedPromise = Promise.resolve(null);

@Directive({
  selector: '[appForm]',
})
export class AppFormDirective implements OnInit, OnDestroy {

  private static readonly FORM_ARRAY_NAME = 'CHILD_FORMS';
  private readonly scrollIntoViewOptions = { block: 'start', behavior: 'smooth' };
  private currentForm: UntypedFormGroup;

  @Input()
  private formGroup: UntypedFormGroup;

  @Input()
    formName: string;

  @Input('appFormGroupName')
  private groupName = '';

  @Input('appFormArrayName')
  private arrayName = AppFormDirective.FORM_ARRAY_NAME;

  @Input()
  private needValidate = true;

  @Input()
  private isRoot = false;

  constructor(
    @SkipSelf()
    @Optional()
    private parentForm: AppFormDirective, // for nestable forms
    private injector: Injector,
    private element: ElementRef
  ) { }

  ngOnInit(): void {
    if (!this.currentForm) {
      // NOTE: at this point both NgForm and ReactiveFrom should be available
      this.executePostponed(() => this.resolveAndRegister());
    }
  }

  ngOnDestroy() {
    this.executePostponed(() => {
      if (this.parentForm) {
        this.parentForm.removeControl(this.currentForm, this.groupName, this.arrayName);
      }
    });
  }

  @HostListener('submit') onsubmit() {
    if (this.needValidate) {
      this.validate();
    }
  }

  public registerNestedForm(control: AbstractControl, childFormGroupName?: string, childFormArrayName?: string): void {
    // NOTE: prevent circular reference (adding to itself)
    if (control === this.currentForm) {
      throw new Error('Trying to add itself! Nestable form can be added only on parent "NgForm" or "FormGroup".');
    }

    if (childFormGroupName) {
      this.currentForm.addControl(childFormGroupName, control);
    } else {
      const childArray: UntypedFormArray = this.currentForm.get(childFormArrayName) as UntypedFormArray;
      if (!childArray) {
        this.currentForm.addControl(childFormArrayName, new UntypedFormArray([]));
      }
      childArray.push(control);
    }
  }

  public removeControl(control: AbstractControl, childFormGroupName?: string, childFormArrayName?: string): void {
    const childArray: UntypedFormArray = this.currentForm.get(childFormArrayName) as UntypedFormArray;

    if (childFormGroupName && this.currentForm.get(childFormGroupName) !== null) {
      // Group, Control
      this.currentForm.removeControl(childFormGroupName);

    } else if (childArray !== null) {
      // Array
      const idx = childArray.controls.indexOf(control);
      childArray.removeAt(idx);
    }
  }

  public validate(scroll = true, path?: string): void {
    const formGroup: UntypedFormGroup = path ? this.formGroup.get(path) as UntypedFormGroup : this.formGroup;
    this.touchInvalidFields(formGroup);
    if (this.formGroup.invalid && scroll) {
      if (!this.scrollToFirstInvalidField()) {
        setTimeout(() => this.scrollToFirstInvalidMessage(), 0);
      }
    }
  }

  private resolveAndRegister(): void {
    this.currentForm = this.resolveCurrentForm();
    this.registerToParent();
  }

  private resolveCurrentForm(): UntypedFormGroup {
    // NOTE: template-driven or model-driven => determine by the formGroup input
    return this.formGroup ? this.formGroup : this.injector.get(NgForm).control;
  }

  private registerToParent(): void {
    if (this.parentForm !== null && !this.isRoot) {
      this.parentForm.registerNestedForm(this.currentForm, this.groupName, this.arrayName);
    }
  }

  private executePostponed(callback: () => void): void {
    resolvedPromise.then(() => callback());
  }

  private markAsEdited(control: AbstractControl, self = true): void {
    control.markAsTouched({ onlySelf: self });
    control.markAsDirty({ onlySelf: self });
    control.updateValueAndValidity({ onlySelf: self });
  }

  /**
   * Összes invalid mező érintése a validációs hibaüzenetek megjelenítéséhez
   *
   * @method touchInvalidFields
   * @access private
   */
  private touchInvalidFields(formGroup: UntypedFormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control.valid) {
        return;
      }

      if (control instanceof UntypedFormControl) {
        this.markAsEdited(control);

      } else if (control instanceof UntypedFormGroup) {
        this.markAsEdited(control);
        this.touchInvalidFields(control);

      } else if (control instanceof UntypedFormArray) {
        this.markAsEdited(control);

        for (const iterator of control.controls) {
          if (iterator.valid) {
            continue;
          }

          if (iterator instanceof UntypedFormControl) {
            this.markAsEdited(control);

          } else if (iterator instanceof UntypedFormGroup) {
            this.touchInvalidFields(iterator);
          }
        }
      }
    });
  }

  /**
   * Az első invalid input mezőhöz scrollozza a képernyőt
   * {@link} Scrolling based on this: http://iamdustan.com/smoothscroll/
   *
   * @method scrollToFirstInvalidField
   * @access private
   */
  private scrollToFirstInvalidField(): boolean {
    const invalidField = this.element.nativeElement.querySelector(`.${AppFormControlDirective.INVALID_INPUT_CLASS}`);
    if (invalidField) {
      const fieldLabel = invalidField.closest('label');
      if (fieldLabel) {
        fieldLabel.scrollIntoView(this.scrollIntoViewOptions);
      } else {
        invalidField.scrollIntoView(this.scrollIntoViewOptions);
      }
      return true;
    }
    return false;
  }

  /**
   * Az első aktív AppShowErrorsDirective-hez scrollozza a képernyőt
   * {@link} Scrolling based on this: http://iamdustan.com/smoothscroll/
   *
   * @method scrollToFirstInvalidMessage
   * @access private
   */
  private scrollToFirstInvalidMessage(): boolean {
    const invalidMessage = this.element.nativeElement.querySelector(`.${AppShowErrorsDirective.INVALID_INPUT_CLASS}`);
    if (invalidMessage) {
      invalidMessage.scrollIntoView({ behavior: 'smooth' });
      return true;
    }
    return false;
  }

}
